import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import api from './services/api';
import { Layout } from './components/Layout';
import { EmployerCockpitLayout } from './components/EmployerCockpitLayout';
import { ZOEKEN_PAD } from './components/EmployerCockpitSidebar';
import { 
  LandingPage,
  LoginPage,
  RegisterPage,
  DashboardPage,
  // Opdrachten pages (used when an employer starts an opdracht from a professional detail page)
  OpdrachtCreatePage,
  VacaturesPage,
  ProfilePage,
  ProfessionalWelcomePage,
  ContractAgreementPage,
  ComplianceElearningPage,
  ProfessionalProfilePage,
  ProfessionalPortfolioPreviewPage,
  ProfessionalDetailPage,
  EmployerProfilePage,
  EmployerContractPage,
  EmployerProfessionalSearchPage,
  EmployerSearchResultsPage,
  EmployerMapSearchPage,
  EmployerVacancyApplicationsPage,
  TermsPage,
  PrivacyPage,
  ScreeningWizardPage,
  ScreeningPendingPage,
  AdminScreeningReviewPage,
  ApproachPage,
  AboutPage,
  ForProfessionalsPage,
  ForPartnersPage,
  EmployerScreeningPage,
  EmployerConnectionsPage,
  EmployerPoolSetupPage,
  EmployerPoolUitnodigenPage,
  MijnBelagomPage,
  ProfessionalConnectionsPage,
  EmployerDossiersPage,
  EmployerCockpitPage,
  EmployerTeamPage,
  EmployerInvitePage,
  ProfessionalHoursPage,
  ProfessionalDossiersPage,
  ProfessionalDossierDetailPage,
  ProfessionalCreateVerslagPage,
  ProfessionalReportsPage,
  ConnectionAgreementPage,
  EmployerConnectionAgreementPage,
  CommunityPage,
  ConnectMeetPage,
  ConnectMeetCreatePage,
  ProfessionalCockpitPage,
  ProfessionalDocumentsPage,
  ProfessionalSearchRequestsPage,
  DossierVerslagenPage,
  ProfessionalNotesPage,
  ProfessionalActionItemsPage,
  ProfessionalPendingDossiersPage,
  ProfessionalReviewsPage,
  AdminProfilePage,
  DossierReviewPage,
  ProfessionalAgendaPage,
  MedewerkerBoekingPage
} from './pages';
import { CollectiviteitenPage } from './pages/CollectiviteitenPage';
// Medewerkersportaal (3 pijlers) — pseudoniem account, rol 'medewerker'
import { MedewerkerLayout } from './components/MedewerkerLayout';
import { MedewerkerRegistratiePage } from './pages/MedewerkerRegistratiePage';
import { PulseScanPage } from './pages/PulseScanPage';
import { PaspoortVerificatiePage } from './pages/PaspoortVerificatiePage';
import { JaarverslagVerificatiePage } from './pages/JaarverslagVerificatiePage';
import { CertificaatVerificatiePage } from './pages/CertificaatVerificatiePage';
import { MedewerkerDashboardPage } from './pages/MedewerkerDashboardPage';
import { MedewerkerProfessionalsPage } from './pages/MedewerkerProfessionalsPage';
import { MedewerkerVpBoekingPage } from './pages/MedewerkerVpBoekingPage';
import { MedewerkerAcademyPage } from './pages/MedewerkerAcademyPage';
import { MedewerkerAcademyOpleiderPage } from './pages/MedewerkerAcademyOpleiderPage';
import { MedewerkerThermometerPage } from './pages/MedewerkerThermometerPage';
import { MedewerkerBerichtenPage } from './pages/MedewerkerBerichtenPage';
import { MedewerkerDocumentenPage } from './pages/MedewerkerDocumentenPage';
import MedewerkerVerslagenPage from './pages/MedewerkerVerslagenPage';
import MedewerkerKofferPage from './pages/MedewerkerKofferPage';
import VerslagAkkoordPage from './pages/VerslagAkkoordPage';
import ProfessionalVerslagDetailPage from './pages/ProfessionalVerslagDetailPage';
import { EmployerMedewerkersportaalPage } from './pages/EmployerMedewerkersportaalPage';
import { EmployerLopendeTrajectenPage } from './pages/EmployerLopendeTrajectenPage';
import EmployerTrajectenAgendaPage from './pages/EmployerTrajectenAgendaPage';
import EmployerGecontracteerdeProfessionalsPage from './pages/EmployerGecontracteerdeProfessionalsPage';
import EmployerTrajectenGespreksverslagenPage from './pages/EmployerTrajectenGespreksverslagenPage';
import EmployerTrajectenJaarverslagenPage from './pages/EmployerTrajectenJaarverslagenPage';
import DataVerzuimPage from './pages/data/DataVerzuimPage';
import DataCrisisPage from './pages/data/DataCrisisPage';
import DataBudgettenPage from './pages/data/DataBudgettenPage';
import DataCultuurPage from './pages/data/DataCultuurPage';
import DataPoolPage from './pages/data/DataPoolPage';
import ProfessionalB2bAgendaPage from './pages/ProfessionalB2bAgendaPage';
import ProfessionalAdhocUrenPage from './pages/ProfessionalAdhocUrenPage';
import ProfessionalAdhocMeldingPage from './pages/ProfessionalAdhocMeldingPage';
import ProfessionalJaarverslagPage from './pages/ProfessionalJaarverslagPage';
import { AdhocAkkoordPage } from './pages/AdhocAkkoordPage';
import { AgendaUitnodigingPage } from './pages/AgendaUitnodigingPage';
import { EmployerActiepuntenPage } from './pages/EmployerActiepuntenPage';
import { EmployerSignalenPage } from './pages/EmployerSignalenPage';
import { EmployerZoekenPage } from './pages/EmployerZoekenPage';
import { EmployerArboPotPage } from './pages/EmployerArboPotPage';
import { CommunityChannelPage } from './pages/CommunityChannelPage';
import { CommunityPostDetailPage } from './pages/CommunityPostDetailPage';
import { CommunityNewPostPage } from './pages/CommunityNewPostPage';
import { IntervisieGroepenPage } from './pages/IntervisieGroepenPage';
import { CommunityProfessionalsPage } from './pages/CommunityProfessionalsPage';
import { IntervisieGroepDetailPage } from './pages/IntervisieGroepDetailPage';
import { IntervisieGroepAanmeldenPage } from './pages/IntervisieGroepAanmeldenPage';
import { IntervisieSessionRegistreerPage } from './pages/IntervisieSessionRegistreerPage';
import { NewsroomPage } from './pages/NewsroomPage';
import { NewsroomChannelPage } from './pages/NewsroomChannelPage';
import { NewsroomArticlePage } from './pages/NewsroomArticlePage';
import { NewsroomNewArticlePage } from './pages/NewsroomNewArticlePage';
import { LinkedinCallback } from './pages';
// Opdrachten pages removed - platform now uses direct professional-employer matching
import ProfessionalListPage from './pages/ProfessionalListPage';
import BerichtenPage from './pages/BerichtenPage';
import ProfessionalChatPage from './pages/ProfessionalChatPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { BackupPage } from './pages/BackupPage';
import AdminInvoicesPage from './pages/AdminInvoicesPage';
import AdminStripeIncidentsPage from './pages/AdminStripeIncidentsPage';
import AdminKlachtenPage from './pages/AdminKlachtenPage';
import { KlachtenSectie } from './components/klachten/KlachtenSectie';
import AdminSbiCodesPage from './pages/AdminSbiCodesPage';
import AdminAcademySubcategorieenPage from './pages/AdminAcademySubcategorieenPage';
import AdminAcademyAuditPage from './pages/AdminAcademyAuditPage';
import AdminKwaliteitspaspoortenPage from './pages/AdminKwaliteitspaspoortenPage';
import AdminCompliancePage from './pages/AdminCompliancePage';
import ProfessionalInvoicesPage from './pages/ProfessionalInvoicesPage';
import EmployerInvoicesPage from './pages/EmployerInvoicesPage';
import EmployerMijlpaalFiatPage from './pages/EmployerMijlpaalFiatPage';
import ProfessionalProjectenPage from './pages/ProfessionalProjectenPage';
import EmployerProjectenPage from './pages/EmployerProjectenPage';
import EmployerProjectCreatePage from './pages/EmployerProjectCreatePage';
import ProfessionalProjectVoorstellenPage from './pages/ProfessionalProjectVoorstellenPage';
import { AdminSearchRequestsPage } from './pages';
import { VacancyDetailPage } from './pages';
import { BelastingdienstPage } from './pages/BelastingdienstPage';
import { ProfessionalAcademyPage } from './pages/ProfessionalAcademyPage';
import { ProfessionalAcademyOpleiderPage } from './pages/ProfessionalAcademyOpleiderPage';
import { OpleidingDetailPage } from './pages/OpleidingDetailPage';
import { MijnOpleidingenPage } from './pages/MijnOpleidingenPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { OpleiderOnboardingPage, OPLEIDER_ONBOARDING_QUERY } from './pages/OpleiderOnboardingPage';
import { getOnboardingState } from './services/opleiderOnboardingApi';
import { OpleiderDashboardPage } from './pages/OpleiderDashboardPage';
import { OpleiderTrainingFormPage } from './pages/OpleiderTrainingFormPage';
import { OpleiderRegistratiesPage } from './pages/OpleiderRegistratiesPage';
import { OpleiderChatPage } from './pages/OpleiderChatPage';
import { EmployerB2bAcademyPage } from './pages/EmployerB2bAcademyPage';
import { OpleiderOfferteAanvragenPage } from './pages/OpleiderOfferteAanvragenPage';
import { OpleiderTrainingenOverzichtPage } from './pages/OpleiderTrainingenOverzichtPage';
import { OpleiderPreviewZandbakPage } from './pages/OpleiderPreviewZandbakPage';
import { OpleiderTeamPage } from './pages/OpleiderTeamPage';
import { OpleiderDataPage } from './pages/OpleiderDataPage';
import { OpleiderFinancePage } from './pages/OpleiderFinancePage';
import { OpleiderInvitePage } from './pages/OpleiderInvitePage';
import { ByopInvitePage } from './pages/ByopInvitePage';
import { EmployerByopPage } from './pages/EmployerByopPage';
import { ByoeInvitePage } from './pages/ByoeInvitePage';
import { ProfessionalByoePage } from './pages/ProfessionalByoePage';
import { OpleiderCockpitLayout } from './components/opleiderCockpit/OpleiderCockpitLayout';
import { ProfessionalDashboardPage } from './pages/ProfessionalDashboardPage';
import ProfessionalWerkplekActiepuntenPage from './pages/ProfessionalWerkplekActiepuntenPage';
import PersonalTodoArchive from './pages/PersonalTodoArchive';
import ProfessionalWerkplekSignalenPage from './pages/ProfessionalWerkplekSignalenPage';
import ProfessionalWerkplekOpdrachtenPage from './pages/ProfessionalWerkplekOpdrachtenPage';
import ProfessionalDataPage from './pages/ProfessionalDataPage';
import ProfessionalContactPage from './pages/ProfessionalContactPage';
import { EmployerOnboardingPage } from './pages/EmployerOnboardingPage';
import { ProfessionalBelagomPage } from './pages/ProfessionalBelagomPage';

//AdminPage Protected Route Component (admin only)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Opdrachtgever-onboarding gate: hoofdgebruiker zonder afgeronde onboarding
// wordt naar de wizard gestuurd voordat de cockpit opent.
const EmployerOnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me').then((res) => res.data.data),
    enabled: user?.role === 'opdrachtgever',
    staleTime: 5 * 60 * 1000,
  });

  const status = meData?.profile?.onboarding_status;
  if (user?.role === 'opdrachtgever' && status && status !== 'company_active') {
    return <Navigate to="/employer/onboarding" replace />;
  }
  return <>{children}</>;
};

// Opleider Rechten-Grendel: zonder ONBOARDED_ACTIVE geen toegang tot de cockpit
// (Menu 1 t/m 5); de server blokkeert dezelfde endpoints (vereisActieveOpleider).
const OpleiderOnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const isOpleider = String(user?.role) === 'opleider';
  const { data, isLoading } = useQuery({
    queryKey: OPLEIDER_ONBOARDING_QUERY,
    queryFn: () => getOnboardingState(),
    enabled: isOpleider,
    staleTime: 60 * 1000,
  });
  if (!isOpleider) return <>{children}</>;
  if (isLoading) return null;
  if (data && data.status !== 'ONBOARDED_ACTIVE') return <Navigate to="/opleider/onboarding" replace />;
  return <>{children}</>;
};

// Berichten met cockpit layout voor opdrachtgevers
const BerichtenWithEmployerLayout: React.FC = () => {
  const { user } = useAuthStore();
  const isEmployer = ['opdrachtgever', 'employer_member'].includes(user?.role ?? '');
  return isEmployer ? (
    <EmployerCockpitLayout>
      <BerichtenPage />
    </EmployerCockpitLayout>
  ) : (
    <BerichtenPage />
  );
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    // If the authenticated user is an admin, send them to the admin dashboard.
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'opleider') return <Navigate to="/opleider/dashboard" replace />;
    if (user?.role === 'medewerker') return <Navigate to="/medewerker" replace />;
    if (user?.role === 'opdrachtgever' || user?.role === 'employer_member') return <Navigate to="/employer/dashboard" replace />;
    return <Navigate to="/professional/dashboard" replace />;
  }

  return <>{children}</>;
};

// Medewerkersportaal: alleen rol 'medewerker'; andere rollen terug naar hun eigen start
const MedewerkerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'medewerker') return <Navigate to="/" replace />;
  return <MedewerkerLayout>{children}</MedewerkerLayout>;
};

/**
 * Oud adres → Dashboard › Professional zoeken (besluit 9). Neemt :id, de
 * querystring en de navigatie-state mee, zodat bestaande links, mails en
 * terugknoppen (zoekcriteria) blijven werken.
 */
const OudZoekAdres: React.FC<{ naar: (id?: string) => string }> = ({ naar }) => {
  const { id } = useParams();
  const location = useLocation();
  const [pad, query] = naar(id).split('?');
  const search = [query, location.search.replace(/^\?/, '')].filter(Boolean).join('&');
  return <Navigate to={{ pathname: pad, search: search ? `?${search}` : '' }} state={location.state} replace />;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/werkwijze" element={<ApproachPage />} />
        <Route path="/over" element={<AboutPage />} />
        <Route path="/voor-professionals" element={<ForProfessionalsPage />} />
        <Route path="/voor-partners" element={<ForPartnersPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Backup download - beveiligd met wachtwoord + toegestane emails */}
        <Route path="/backup" element={<BackupPage />} />
        {/* Public review form (no login required) */}
        <Route path="/review/:token" element={<DossierReviewPage />} />
        {/* Anoniem medewerkerportaal: afspraak inplannen via aflopende link */}
        <Route path="/plannen/:token" element={<MedewerkerBoekingPage />} />
        {/* Anonieme Bedrijfsthermometer-scan via eenmalige token (migratie 148) */}
        <Route path="/scan/:token" element={<PulseScanPage />} />
        <Route path="/s/:token" element={<PulseScanPage />} />
        {/* Ad-hoc uren accorderen zonder login: SMS-link (medewerker) en e-maillink (contactpersoon), migratie 163 */}
        <Route path="/u/:token" element={<AdhocAkkoordPage />} />
        <Route path="/uren-akkoord/:token" element={<AdhocAkkoordPage />} />
        {/* Agenda Matrix, actie-pad C: externe genodigde bevestigt zonder login (migratie 192) */}
        <Route path="/uitnodiging/:token" element={<AgendaUitnodigingPage />} />
        {/* Publieke QR-verificatie Kwaliteitspaspoort (migratie 151) */}
        <Route path="/verificatie/paspoort/:code" element={<PaspoortVerificatiePage />} />
        <Route path="/verify/paspoort/:code" element={<PaspoortVerificatiePage />} />
        {/* Publieke QR-verificatie geconsolideerd jaarverslag (migratie 174) */}
        <Route path="/verificatie/jaarverslag/:code" element={<JaarverslagVerificatiePage />} />
        {/* Publieke hash-verificatie Beroepsregistratiecertificaat (Professional Compliance Ledger) */}
        <Route path="/verify/certificaat/:hash" element={<CertificaatVerificatiePage />} />
        <Route path="/verificatie/certificaat/:hash" element={<CertificaatVerificatiePage />} />
        {/* Medewerkersportaal: registratie met toegangscode + 3 pijlers */}
        <Route path="/medewerker/registreren" element={<PublicRoute><MedewerkerRegistratiePage /></PublicRoute>} />
        <Route path="/medewerker" element={<MedewerkerRoute><MedewerkerDashboardPage /></MedewerkerRoute>} />
        <Route path="/medewerker/professionals" element={<MedewerkerRoute><MedewerkerProfessionalsPage /></MedewerkerRoute>} />
        <Route path="/medewerker/vp/:meldingId" element={<MedewerkerRoute><MedewerkerVpBoekingPage /></MedewerkerRoute>} />
        <Route path="/medewerker/academy" element={<MedewerkerRoute><MedewerkerAcademyPage /></MedewerkerRoute>} />
        <Route path="/medewerker/academy/opleider/:id" element={<MedewerkerRoute><MedewerkerAcademyOpleiderPage /></MedewerkerRoute>} />
        <Route path="/medewerker/thermometer" element={<MedewerkerRoute><MedewerkerThermometerPage /></MedewerkerRoute>} />
        <Route path="/medewerker/berichten" element={<MedewerkerRoute><MedewerkerBerichtenPage /></MedewerkerRoute>} />
        <Route path="/medewerker/documenten" element={<MedewerkerRoute><MedewerkerDocumentenPage /></MedewerkerRoute>} />
        <Route path="/medewerker/verslagen" element={<MedewerkerRoute><MedewerkerVerslagenPage /></MedewerkerRoute>} />
        <Route path="/medewerker/koffer" element={<MedewerkerRoute><MedewerkerKofferPage /></MedewerkerRoute>} />
        {/* Two-gate toestemmingslink uit de e-mail; publiek, ook voor ingelogde gebruikers */}
        <Route path="/verslag-akkoord/:token" element={<VerslagAkkoordPage />} />
        {/* Employer invite - set password and activate access */}
        <Route path="/invite/employer/:token" element={<EmployerInvitePage />} />
        {/* Opleider-team invite (migratie 150) */}
        <Route path="/invite/opleider/:token" element={<OpleiderInvitePage />} />
        {/* Bring Your Own Professional: uitnodiging van een opdrachtgever (migratie 164) */}
        <Route path="/invite/byop/:token" element={<ByopInvitePage />} />
        <Route path="/invite/byoe/:token" element={<ByoeInvitePage />} />
        {/* Vacatures route (replaces opdrachten) */}
        <Route 
          path="/vacatures" 
          element={
            <ProtectedRoute>
              <VacaturesPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/vacatures/:id"
          element={
            <ProtectedRoute>
              <VacancyDetailPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/opdrachten" 
          element={
            <ProtectedRoute>
              <VacaturesPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/opdrachten/:id"
          element={
            <ProtectedRoute>
              <VacancyDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/opdrachten/nieuw"
          element={
            <ProtectedRoute>
              <OpdrachtCreatePage />
            </ProtectedRoute>
          }
        />
        
        {/* Professional list route */}
        <Route 
          path="/professionals" 
          element={
            <ProtectedRoute>
              <ProfessionalListPage />
            </ProtectedRoute>
          }
        />
        
        {/* Berichten route - cockpit layout voor opdrachtgevers */}
        <Route 
          path="/berichten" 
          element={
            <ProtectedRoute>
              <BerichtenWithEmployerLayout />
            </ProtectedRoute>
          }
        />
        
        {/* Professional chat route - professionals onderling chatten */}
        <Route
          path="/professionals/chat"
          element={
            <ProtectedRoute>
              <ProfessionalChatPage />
            </ProtectedRoute>
          }
        />

        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        {/* General profile route */}
        <Route 
          path="/profiel" 
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        
        {/* Professional routes */}
        <Route 
          path="/professional/screening" 
          element={
            <ProtectedRoute>
              <ScreeningWizardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/screening/pending" 
          element={
            <ProtectedRoute>
              <ScreeningPendingPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/welcome" 
          element={
            <ProtectedRoute>
              <ProfessionalWelcomePage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/professional/onboarding"
          element={<Navigate to="/professional/screening" replace />}
        />
        <Route
          path="/professional/compliance-elearning"
          element={
            <ProtectedRoute>
              <ComplianceElearningPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/contract"
          element={
            <ProtectedRoute>
              <ContractAgreementPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/professional/profile" 
          element={
            <ProtectedRoute>
              <ProfessionalProfilePage />
            </ProtectedRoute>
          } 
        />
        {/* Live-preview van het eigen portfolio (FO stap 9) */}
        <Route
          path="/professional/portfolio-preview"
          element={
            <ProtectedRoute>
              <ProfessionalPortfolioPreviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/dashboard"
          element={
            <ProtectedRoute>
              <ProfessionalDashboardPage />
            </ProtectedRoute>
          }
        />
        {/* Hoofdmenu professional: Dashboard-submenu (3 banen), Data en Contact-hub */}
        <Route
          path="/professional/dashboard/actiepunten"
          element={
            <ProtectedRoute>
              <ProfessionalWerkplekActiepuntenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/dashboard/todo"
          element={
            <ProtectedRoute>
              <PersonalTodoArchive />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/dashboard/signalen"
          element={
            <ProtectedRoute>
              <ProfessionalWerkplekSignalenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/dashboard/opdrachten"
          element={
            <ProtectedRoute>
              <ProfessionalWerkplekOpdrachtenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/data/:onderdeel?"
          element={
            <ProtectedRoute>
              <ProfessionalDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/contact"
          element={
            <ProtectedRoute>
              <ProfessionalContactPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/cockpit"
          element={
            <ProtectedRoute>
              <ProfessionalCockpitPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/belagom"
          element={
            <ProtectedRoute>
              <ProfessionalBelagomPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/byoe"
          element={
            <ProtectedRoute>
              <ProfessionalByoePage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/professional/meldingen" 
          element={
            <ProtectedRoute>
              <ProfessionalPendingDossiersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/documents" 
          element={
            <ProtectedRoute>
              <ProfessionalDocumentsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/search-requests" 
          element={
            <ProtectedRoute>
              <ProfessionalSearchRequestsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/agenda" 
          element={
            <ProtectedRoute>
              <ProfessionalAgendaPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/professional/agenda/opdrachtgevers"
          element={
            <ProtectedRoute>
              <ProfessionalB2bAgendaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/adhoc-uren"
          element={
            <ProtectedRoute>
              <ProfessionalAdhocUrenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/adhoc-melding"
          element={
            <ProtectedRoute>
              <ProfessionalAdhocMeldingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/jaarverslag"
          element={
            <ProtectedRoute>
              <ProfessionalJaarverslagPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/professional/hours" 
          element={
            <ProtectedRoute>
              <ProfessionalHoursPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/dossiers" 
          element={
            <ProtectedRoute>
              <ProfessionalDossiersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/dossiers/:id" 
          element={
            <ProtectedRoute>
              <ProfessionalDossierDetailPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/dossiers/:dossierId/verslagen/nieuw" 
          element={
            <ProtectedRoute>
              <ProfessionalCreateVerslagPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/dossiers/:id/verslagen/nieuw" 
          element={
            <ProtectedRoute>
              <ProfessionalCreateVerslagPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/professional/dossiers/:dossierId/verslagen/:verslagId"
          element={
            <ProtectedRoute>
              <ProfessionalVerslagDetailPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/professional/connections" 
          element={
            <ProtectedRoute>
              <ProfessionalConnectionsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/connections/:connectionId/agreements" 
          element={
            <ProtectedRoute>
              <ConnectionAgreementPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/reports" 
          element={
            <ProtectedRoute>
              <ProfessionalReportsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/notes" 
          element={
            <ProtectedRoute>
              <ProfessionalNotesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/action-items" 
          element={
            <ProtectedRoute>
              <ProfessionalActionItemsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/professional/reviews" 
          element={
            <ProtectedRoute>
              <ProfessionalReviewsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/community" 
          element={
            <ProtectedRoute>
              <CommunityPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/community/professionals"
          element={
            <ProtectedRoute>
              <CommunityProfessionalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/kanaal/:slug"
          element={
            <ProtectedRoute>
              <CommunityChannelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/bericht/:id"
          element={
            <ProtectedRoute>
              <CommunityPostDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/nieuw-bericht"
          element={
            <ProtectedRoute>
              <CommunityNewPostPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intervisie"
          element={
            <ProtectedRoute>
              <IntervisieGroepenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intervisie/aanmelden"
          element={
            <ProtectedRoute>
              <IntervisieGroepAanmeldenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intervisie/:id"
          element={
            <ProtectedRoute>
              <IntervisieGroepDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intervisie/:id/sessie-registreren"
          element={
            <ProtectedRoute>
              <IntervisieSessionRegistreerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsroom"
          element={
            <ProtectedRoute>
              <NewsroomPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsroom/kanaal/:slug"
          element={
            <ProtectedRoute>
              <NewsroomChannelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsroom/artikel/:id"
          element={
            <ProtectedRoute>
              <NewsroomArticlePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsroom/nieuw-artikel"
          element={
            <ProtectedRoute>
              <NewsroomNewArticlePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connect-meet"
          element={
            <ProtectedRoute>
              <ConnectMeetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connect-meet/nieuw"
          element={
            <ProtectedRoute>
              <ConnectMeetCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connect-meet/:id/bewerken"
          element={
            <ProtectedRoute>
              <ConnectMeetCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collectiviteiten"
          element={
            <ProtectedRoute>
              <CollectiviteitenPage />
            </ProtectedRoute>
          }
        />
        
        {/* Employer routes */}
        <Route
          path="/employer/onboarding"
          element={
            <ProtectedRoute>
              <EmployerOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/cockpit"
          element={
            <ProtectedRoute>
              <EmployerOnboardingGate>
                <EmployerCockpitPage />
              </EmployerOnboardingGate>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/employer/profile" 
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerProfilePage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/employer/contract"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerContractPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/pool-setup"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerPoolSetupPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/byop"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerByopPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/pool-uitnodigingen"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerPoolUitnodigenPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        {/* Besluit 9: zoeken, kaart en vacatures vallen onder Dashboard › Professional zoeken; oude adressen verwijzen door */}
        <Route path="/employer/search-professionals" element={<OudZoekAdres naar={() => `${ZOEKEN_PAD}/uitgebreid`} />} />
        <Route path="/employer/search-results" element={<OudZoekAdres naar={() => `${ZOEKEN_PAD}/resultaten`} />} />
        <Route path="/employer/map-search" element={<OudZoekAdres naar={() => `${ZOEKEN_PAD}/kaart`} />} />
        <Route path="/employer/vacancies" element={<OudZoekAdres naar={() => `${ZOEKEN_PAD}?route=vacatures`} />} />
        <Route path="/employer/vacancies/:id/bewerken" element={<OudZoekAdres naar={(id) => `${ZOEKEN_PAD}/vacatures/${id}/bewerken`} />} />
        <Route path="/employer/vacancies/:id/sollicitaties" element={<OudZoekAdres naar={(id) => `${ZOEKEN_PAD}/vacatures/${id}/sollicitaties`} />} />
        {[
          { pad: 'uitgebreid', pagina: <EmployerProfessionalSearchPage /> },
          { pad: 'resultaten', pagina: <EmployerSearchResultsPage /> },
          { pad: 'kaart', pagina: <EmployerMapSearchPage /> },
          { pad: 'vacatures/:id/bewerken', pagina: <OpdrachtCreatePage /> },
          { pad: 'vacatures/:id/sollicitaties', pagina: <EmployerVacancyApplicationsPage /> },
        ].map(({ pad, pagina }) => (
          <Route
            key={`zoeken-${pad}`}
            path={`${ZOEKEN_PAD}/${pad}`}
            element={
              <ProtectedRoute>
                <EmployerCockpitLayout>{pagina}</EmployerCockpitLayout>
              </ProtectedRoute>
            }
          />
        ))}
        <Route 
          path="/employer/screening" 
          element={
            <ProtectedRoute>
              <EmployerScreeningPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer/connections" 
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerConnectionsPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/employer/connections/:connectionId/agreements"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerConnectionAgreementPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/employer/dossiers" 
          element={
            <ProtectedRoute>
              <EmployerDossiersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer/dossiers/:dossierId/verslagen" 
          element={
            <ProtectedRoute>
              <DossierVerslagenPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/employer/mijn-belagom"
          element={
            <ProtectedRoute>
              {/* Mijn Cockpit: stappenmenu 1-8 neemt de linkerkolom over i.p.v. de algemene zijbalk */}
              <EmployerCockpitLayout zijbalk={false}>
                <MijnBelagomPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/medewerkersportaal"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerMedewerkersportaalPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        {/* Hoofdmenu Dashboard (Onderdeel 50B): vier banen in de linkerkolom; landingspagina na inloggen = Actiepunten */}
        <Route path="/employer/dashboard" element={<Navigate to="/employer/dashboard/actiepunten" replace />} />
        {[
          { pad: 'dashboard/actiepunten', pagina: <EmployerActiepuntenPage /> },
          { pad: 'dashboard/signalen', pagina: <EmployerSignalenPage /> },
          { pad: 'dashboard/zoeken', pagina: <EmployerZoekenPage /> },
          { pad: 'arbo-pot', pagina: <EmployerArboPotPage /> },
        ].map(({ pad, pagina }) => (
          <Route
            key={pad}
            path={`/employer/${pad}`}
            element={
              <ProtectedRoute>
                <EmployerCockpitLayout>{pagina}</EmployerCockpitLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Hoofdmenu Trajecten: eigen linker-submenu (EmployerCockpitSidebar → TRAJECTEN_ITEMS) */}
        <Route path="/employer/trajecten" element={<Navigate to="/employer/trajecten/agenda" replace />} />
        {[
          { pad: 'agenda', pagina: <EmployerTrajectenAgendaPage /> },
          { pad: 'lopend', pagina: <EmployerLopendeTrajectenPage /> },
          { pad: 'professionals', pagina: <EmployerGecontracteerdeProfessionalsPage /> },
          { pad: 'mijlpalen', pagina: <EmployerMijlpaalFiatPage /> },
          { pad: 'facturen', pagina: <EmployerInvoicesPage /> },
          { pad: 'gespreksverslagen', pagina: <EmployerTrajectenGespreksverslagenPage /> },
          { pad: 'jaarverslagen', pagina: <EmployerTrajectenJaarverslagenPage /> },
        ].map(({ pad, pagina }) => (
          <Route
            key={pad}
            path={`/employer/trajecten/${pad}`}
            element={
              <ProtectedRoute>
                <EmployerCockpitLayout>{pagina}</EmployerCockpitLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Hoofdmenu Data (Menu 4): vijf submappen + universele datumfilter (EmployerCockpitSidebar → DATA_ITEMS) */}
        <Route path="/employer/data" element={<Navigate to="/employer/data/verzuim" replace />} />
        {[
          { pad: 'verzuim', pagina: <DataVerzuimPage /> },
          { pad: 'crisis', pagina: <DataCrisisPage /> },
          { pad: 'budgetten', pagina: <DataBudgettenPage /> },
          { pad: 'cultuur', pagina: <DataCultuurPage /> },
          { pad: 'pool', pagina: <DataPoolPage /> },
        ].map(({ pad, pagina }) => (
          <Route
            key={`data-${pad}`}
            path={`/employer/data/${pad}`}
            element={
              <ProtectedRoute>
                <EmployerCockpitLayout>{pagina}</EmployerCockpitLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Lopende Trajecten & Regie (129): vier strikt gescheiden datatables */}
        <Route
          path="/employer/lopende-trajecten"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerLopendeTrajectenPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/team"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerTeamPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer/insights" 
          element={<Navigate to="/employer/data/verzuim" replace />} 
        />
        <Route 
          path="/professionals/:id" 
          element={
            <ProtectedRoute>
              <ProfessionalDetailPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer/professional/:id" 
          element={
            <ProtectedRoute>
              <ProfessionalDetailPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          } 
        />
        <Route
          path="/admin/invoices"
          element={
            <AdminRoute>
              <AdminInvoicesPage />
            </AdminRoute>
          } 
        />
        <Route
          path="/admin/stripe-incidenten"
          element={
            <AdminRoute>
              <AdminStripeIncidentsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/klachten"
          element={
            <AdminRoute>
              <AdminKlachtenPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/sbi-codes"
          element={
            <AdminRoute>
              <AdminSbiCodesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/academy-subcategorieen"
          element={
            <AdminRoute>
              <AdminAcademySubcategorieenPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/academy-audit"
          element={
            <AdminRoute>
              <AdminAcademyAuditPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/kwaliteitspaspoorten"
          element={
            <AdminRoute>
              <AdminKwaliteitspaspoortenPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/compliance"
          element={
            <AdminRoute>
              <AdminCompliancePage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <AdminRoute>
              <AdminProfilePage />
            </AdminRoute>
          }
        />
        <Route 
          path="/admin/screenings" 
          element={
            <AdminRoute>
              <AdminScreeningReviewPage />
            </AdminRoute>
          } 
        />
        <Route
          path="/admin/search-requests"
          element={
            <AdminRoute>
              <AdminSearchRequestsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/belastingdienst"
          element={
            <AdminRoute>
              <BelastingdienstPage />
            </AdminRoute>
          }
        />

        {/* Professional/Employer invoice routes */}
        <Route
          path="/professional/invoices"
          element={
            <ProtectedRoute>
              <ProfessionalInvoicesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/invoices"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerInvoicesPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />

        {/* Check & Fiat: HR accordeert projectmijlpalen vóór facturatie (stroom 3) */}
        <Route
          path="/employer/mijlpalen"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerMijlpaalFiatPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />

        {/* Projecttraject (114): HR-dashboard, projectstart en voorstellen */}
        <Route
          path="/employer/projecten"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerProjectenPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/projecten/nieuw"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerProjectCreatePage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/professional/projectvoorstellen"
          element={
            <ProtectedRoute>
              <ProfessionalProjectVoorstellenPage />
            </ProtectedRoute>
          }
        />

        {/* Projectcockpit van de professional: uren schrijven op mijlpalen */}
        <Route
          path="/professional/projecten"
          element={
            <ProtectedRoute>
              <ProfessionalProjectenPage />
            </ProtectedRoute>
          }
        />
        
        {/* Opleidingen routes (professionals) */}
        {/* Professional Academy: expertportaal in Netflix-stijl (+ Laag C opleidersprofiel) */}
        <Route
          path="/opleidingen"
          element={
            <ProtectedRoute>
              <ProfessionalAcademyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/opleidingen/opleider/:id"
          element={
            <ProtectedRoute>
              <ProfessionalAcademyOpleiderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/opleidingen/:id"
          element={
            <ProtectedRoute>
              <OpleidingDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mijn-opleidingen"
          element={
            <ProtectedRoute>
              <MijnOpleidingenPage />
            </ProtectedRoute>
          }
        />

        {/* Opleider (training provider) routes — cockpit met 5 hoofdmenu's (opleiderMenu.ts) */}
        <Route path="/opleider/register" element={<OpleiderOnboardingPage />} />
        <Route path="/opleider/onboarding" element={<OpleiderOnboardingPage />} />
        {([
          // Menu 1 · Dashboard (OWNER, ADMINISTRATOR)
          ['/opleider/dashboard', <OpleiderDashboardPage />],
          ['/opleider/dashboard/cursisten', <OpleiderRegistratiesPage />],
          ['/opleider/dashboard/offerte-aanvragen', <OpleiderOfferteAanvragenPage />],
          // Menu 2 · Academy Management
          ['/opleider/academy/cursus-invoeren', <OpleiderTrainingFormPage />],
          ['/opleider/trainings/nieuw', <OpleiderTrainingFormPage />],
          ['/opleider/trainings/:id/bewerken', <OpleiderTrainingFormPage />],
          ['/opleider/academy/trainingen', <OpleiderTrainingenOverzichtPage />],
          ['/opleider/academy/preview', <OpleiderPreviewZandbakPage />],
          ['/opleider/academy/team', <OpleiderTeamPage />],
          // Menu 3 · Data & Quality (OWNER, ADMINISTRATOR)
          ['/opleider/data/analytics', <OpleiderDataPage view="analytics" />],
          ['/opleider/data/reviews', <OpleiderDataPage view="reviews" />],
          ['/opleider/data/feedback', <OpleiderDataPage view="feedback" />],
          // Menu 4 · Finance (OWNER)
          ['/opleider/finance/stripe', <OpleiderFinancePage view="stripe" />],
          ['/opleider/finance/ledger', <OpleiderFinancePage view="ledger" />],
          ['/opleider/finance/fees', <OpleiderFinancePage view="fees" />],
          ['/opleider/finance/helpdesk', <div className="px-4 sm:px-6 py-6"><KlachtenSectie /></div>],
          // Menu 5 · Chats
          ['/opleider/chats/cursisten', <OpleiderChatPage tab="cursisten" />],
          ['/opleider/chats/zakelijk', <OpleiderChatPage tab="zakelijk" />],
        ] as Array<[string, React.ReactElement]>).map(([pad, element]) => (
          <Route
            key={pad}
            path={pad}
            element={
              <ProtectedRoute>
                <OpleiderOnboardingGate>
                  <OpleiderCockpitLayout>{element}</OpleiderCockpitLayout>
                </OpleiderOnboardingGate>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Oude opleider-paden → nieuwe cockpit-submenu's */}
        <Route path="/opleider/registraties" element={<Navigate to="/opleider/dashboard/cursisten" replace />} />
        <Route path="/opleider/offerte-aanvragen" element={<Navigate to="/opleider/dashboard/offerte-aanvragen" replace />} />
        <Route path="/opleider/facturen" element={<Navigate to="/opleider/finance/ledger" replace />} />
        <Route path="/opleider/chat" element={<Navigate to="/opleider/chats/cursisten" replace />} />
        <Route path="/opleider/academy" element={<Navigate to="/opleider/academy/trainingen" replace />} />
        <Route path="/opleider/data" element={<Navigate to="/opleider/data/analytics" replace />} />
        <Route path="/opleider/finance" element={<Navigate to="/opleider/finance/stripe" replace />} />
        <Route path="/opleider/chats" element={<Navigate to="/opleider/chats/cursisten" replace />} />

        {/* Employer opleidingen route */}
        <Route
          path="/employer/opleidingen"
          element={
            <ProtectedRoute>
              <EmployerCockpitLayout>
                <EmployerB2bAcademyPage />
              </EmployerCockpitLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="/linkedin-callback" element={<LinkedinCallback />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
